<script setup lang="ts">
/**
 * FormKit input `grouped_toggle` (backend: `GroupedToggleType`): a multiple selection rendered as toggles, grouped
 * into collapsible groups that each have a toggle selecting all of their entries. Shows only `optionsFiltered` if
 * set (see `VbFormFilteredRow`).
 */
import CheckboxToggle from "@/components/form/CheckboxToggle.vue"
import RotateChevron from "@/components/RotateChevron.vue"
import { toEntityIds } from "@/utils/formData"
import type { FormKitFrameworkContext } from "@formkit/core"
import { computed, reactive } from "vue"
import { useI18n } from "vue-i18n"

type Option = {
    value: string
    label: string
    group: string | null
    additionalData?: Record<string, unknown> | null
}

type Group = {
    key: string
    label: string
    options: Option[]
}

const props = defineProps<{
    context: FormKitFrameworkContext
}>()

const i18n = useI18n()

const ungroupedKey = "_ungrouped"

if (Array.isArray(props.context._value) && props.context._value.some(v => typeof v === "object")) {
    props.context.node.input(toEntityIds(props.context._value))
}

const selected = computed<string[]>({
    get: () => toEntityIds(props.context._value),
    set: value => props.context.node.input(value),
})

const visibleOptions = computed(
    () => (props.context.optionsFiltered ?? props.context.options ?? []) as Option[],
)

// Groups keep the order of the options, entries without a group go last
const groups = computed<Group[]>(() => {
    const byKey = new Map<string, Group>()
    for (const option of visibleOptions.value) {
        const key = option.group ?? ungroupedKey
        if (!byKey.has(key)) {
            byKey.set(key, {
                key,
                label: option.group ?? i18n.t("@app:components.grouped_toggle.without_group"),
                options: [],
            })
        }
        byKey.get(key)!.options.push(option)
    }

    return [...byKey.values()].sort((a, b) => Number(a.key === ungroupedKey) - Number(b.key === ungroupedKey))
})

const openGroupKeys = reactive(new Set<string>())

function isOpen(group: Group): boolean {
    return openGroupKeys.has(group.key)
}

function toggleOpen(group: Group) {
    if (!openGroupKeys.delete(group.key)) {
        openGroupKeys.add(group.key)
    }
}

function selectedCount(options: Option[]): number {
    return options.filter(o => selected.value.includes(o.value)).length
}

// Selections of options that are filtered out stay untouched
function setSelected(options: Option[], value: boolean) {
    const values = options.map(o => o.value)
    const withoutOptions = selected.value.filter(v => !values.includes(v))
    selected.value = value ? [...withoutOptions, ...values] : withoutOptions
}
</script>

<template>
    <div>
        <div v-for="group in groups" :key="group.key" class="toggle-group" :class="{ open: isOpen(group) }">
            <div class="toggle-group-header" @click="toggleOpen(group)">
                <div @click.stop>
                    <CheckboxToggle
                        :model-value="selectedCount(group.options) === group.options.length"
                        :indeterminate="selectedCount(group.options) > 0"
                        @update:model-value="setSelected(group.options, $event)"
                    />
                </div>
                <span class="font-weight-bold">{{ group.label }}</span>
                <RotateChevron :open="isOpen(group)" class="ml-auto" />
            </div>
            <div v-animate-show="isOpen(group)">
                <div class="toggle-group-body">
                    <CheckboxToggle
                        v-for="option in group.options"
                        :key="option.value"
                        v-model="selected"
                        :value="option.value"
                        class="toggle-row"
                    >
                        <span>{{ option.label }}</span>
                    </CheckboxToggle>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@import "@/styles/variables";

.toggle-group {
    border: 1px solid $gray-300;
    border-radius: $border-radius;
    margin-bottom: 0.5rem;
    overflow: hidden;
    background-color: $white;
}

.toggle-group-header {
    display: flex;
    align-items: center;
    padding: 0.5rem 1rem;
    background-color: $gray-100;
    cursor: pointer;

    :deep(.checkbox) {
        margin: 0 0.5rem 0 0;
    }
}

.toggle-group.open .toggle-group-header {
    background-color: rgba($primary, 0.1);
    border-bottom: 1px solid $gray-300;
}

.toggle-group-body {
    padding: 0 1rem 0 3rem;
}

.toggle-row {
    margin: 0;
    padding: 0.5rem 0;

    &:not(:last-child) {
        border-bottom: 1px solid $gray-200;
    }
}
</style>
